import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
    HiOutlineBars3,
    HiOutlineShieldCheck,
    HiOutlineXMark,
} from 'react-icons/hi2';
import { Link, useLocation } from 'wouter';

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [location] = useLocation();
  const isHome = location === '/';

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu on route change
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    // Prevent body scroll when mobile menu is open
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const scrolled = isScrolled || !isHome;

  return (
    <nav
      className="fixed top-0 w-full z-50 transition-all duration-500"
      style={{
        background: scrolled ? 'rgba(255,255,255,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(18px) saturate(180%)' : 'none',
        boxShadow: scrolled ? '0 1px 0 rgba(10,37,64,0.08)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 sm:h-20 items-center">
          <Link href="/">
            <motion.span
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="font-bebas text-lg sm:text-xl tracking-[0.08em] cursor-pointer transition-colors duration-300 select-none"
              style={{ color: scrolled ? '#0a2540' : '#ffffff' }}
            >
              windows cleaning <span style={{ color: '#0ea5e9' }}>School</span>
            </motion.span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {[
              { href: '/#wwd', label: 'What We Do' },
              { href: '/#classes', label: 'Classes' },
              { href: '/dashboard', label: 'Dashboard' },
              { href: '/book', label: 'Book' },
              { href: '/admin/proposal', label: 'Proposal' },
            ].map(l => (
              <Link key={l.href} href={l.href}>
                <motion.span
                  whileHover={{ y: -2 }}
                  className="font-sans text-[0.82rem] cursor-pointer transition-colors duration-200 hover:text-[#0ea5e9]"
                  style={{
                    color: scrolled ? '#94a3b8' : 'rgba(255,255,255,0.75)',
                  }}
                >
                  {l.label}
                </motion.span>
              </Link>
            ))}
            <Link href="/admin">
              <motion.span
                whileHover={{ y: -2 }}
                className="font-sans text-[0.82rem] cursor-pointer transition-colors duration-200 hover:text-[#0ea5e9] flex items-center gap-1.5"
                style={{
                  color: scrolled ? '#94a3b8' : 'rgba(255,255,255,0.75)',
                }}
              >
                <HiOutlineShieldCheck className="w-4 h-4" />
                Admin
              </motion.span>
            </Link>
            <Link href="/book">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="font-rajdhani font-bold text-[0.72rem] tracking-[0.18em] uppercase px-6 h-9 rounded-full text-white transition-all duration-200 cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg, #0ea5e9, #0369a1)',
                  boxShadow: '0 4px 16px rgba(14,165,233,0.35)',
                }}
              >
                Enroll Now
              </motion.button>
            </Link>
          </div>

          {/* Mobile toggle */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="lg:hidden transition-colors cursor-pointer p-2 -mr-2 touch-manipulation"
            style={{ color: scrolled ? '#0a2540' : 'white' }}
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <HiOutlineXMark className="w-6 h-6" />
            ) : (
              <HiOutlineBars3 className="w-6 h-6" />
            )}
          </motion.button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden bg-white/98 backdrop-blur-xl border-t border-slate-200/50 shadow-lg overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-6 flex flex-col gap-1">
              {[
                { href: '/', label: 'Home' },
                { href: '/#wwd', label: 'What We Do' },
                { href: '/#classes', label: 'Classes' },
                { href: '/dashboard', label: 'Dashboard' },
                { href: '/book', label: 'Book a Session' },
                { href: '/admin/proposal', label: 'Proposal' },
                { href: '/admin', label: 'Admin Panel' },
              ].map((l, i) => (
                <motion.div
                  key={l.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3 }}
                >
                  <Link href={l.href}>
                    <span
                      className="block py-3 px-4 font-sans text-base text-[#0d1e30] hover:text-[#0ea5e9] hover:bg-[#e0f4fd]/30 rounded-lg transition-all cursor-pointer touch-manipulation active:scale-98"
                      onClick={() => setMobileOpen(false)}
                    >
                      {l.label}
                    </span>
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="mt-4 px-4"
              >
                <Link href="/book">
                  <button
                    className="w-full font-rajdhani font-bold text-sm tracking-[0.18em] uppercase px-6 h-12 rounded-full text-white transition-all duration-200 cursor-pointer touch-manipulation active:scale-95"
                    style={{
                      background: 'linear-gradient(135deg, #0ea5e9, #0369a1)',
                      boxShadow: '0 4px 16px rgba(14,165,233,0.35)',
                    }}
                    onClick={() => setMobileOpen(false)}
                  >
                    Enroll Now
                  </button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export function Footer() {
  return (
    <footer
      style={{ background: '#0a2540' }}
      className="text-white py-12 sm:py-16"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 pb-10 sm:pb-12 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <div className="col-span-1 sm:col-span-2">
            <div className="font-bebas text-2xl tracking-[0.08em] mb-4">
              windows cleaning <span className="text-[#0ea5e9]">School</span>
            </div>
            <p
              className="font-sans font-light text-sm sm:text-[0.85rem] leading-[1.8] max-w-md"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              Elite technical training for high-rise window cleaning
              professionals. We build careers that reach the sky with safety,
              precision, and excellence at every level.
            </p>
          </div>
          <div>
            <div className="font-rajdhani font-bold text-[0.65rem] tracking-[0.3em] uppercase text-[#0ea5e9] mb-4">
              Site
            </div>
            <ul className="flex flex-col gap-3">
              {[
                { href: '/', label: 'Home' },
                { href: '/#classes', label: 'Classes' },
                { href: '/dashboard', label: 'Dashboard' },
                { href: '/book', label: 'Book a Session' },
                { href: '/admin/proposal', label: 'Proposal' },
              ].map(l => (
                <li key={l.href}>
                  <Link href={l.href}>
                    <motion.span
                      whileHover={{ x: 4 }}
                      className="font-sans font-light text-sm sm:text-[0.84rem] cursor-pointer hover:text-[#0ea5e9] transition-colors inline-block"
                      style={{ color: 'rgba(255,255,255,0.45)' }}
                    >
                      {l.label}
                    </motion.span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-rajdhani font-bold text-[0.65rem] tracking-[0.3em] uppercase text-[#0ea5e9] mb-4">
              Contact
            </div>
            <ul
              className="flex flex-col gap-3 font-sans font-light text-sm sm:text-[0.84rem]"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              <li>1-800-HIGHDROP</li>
              <li className="break-all">enroll@windowcleaningschool.com</li>
              <li>Miami, FL — HQ</li>
            </ul>
          </div>
        </div>
        <div className="pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left">
          <span
            className="font-sans text-xs sm:text-[0.78rem]"
            style={{ color: 'rgba(255,255,255,0.3)' }}
          >
            &copy; {new Date().getFullYear()} windows cleaning School. All rights
            reserved.
          </span>
          <span
            className="font-sans text-xs sm:text-[0.78rem]"
            style={{ color: 'rgba(255,255,255,0.2)' }}
          >
            Licensed, insured & certified.
          </span>
        </div>
      </div>
    </footer>
  );
}
